import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Send, Bell, Settings, MailCheck, Power, MessageCircle, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (url, opts = {}) => {
  const token = localStorage.getItem("token");
  return fetch(`${API}${url}`, { ...opts, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) }});
};

export default function CrmPromemoriaConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [testEmail, setTestEmail] = useState("proia.aldo62@gmail.com");
  const [testPhone, setTestPhone] = useState("");
  const [killSwitch, setKillSwitch] = useState(false);
  const [waPending, setWaPending] = useState([]);
  const [loadingWa, setLoadingWa] = useState(false);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [visite, setVisite] = useState([]);
  const [storico, setStorico] = useState([]);
  const [storicoFilter, setStoricoFilter] = useState("all");
  const [storicoFeedback, setStoricoFeedback] = useState("all");
  const [storicoSearch, setStoricoSearch] = useState("");
  const [storicoStats, setStoricoStats] = useState({ total: 0, feedback_received: 0, visited: 0, will_visit: 0, pending: 0 });
  const [destinatari, setDestinatari] = useState([]);
  const [filterTipo, setFilterTipo] = useState("tutti");
  const [filterStato, setFilterStato] = useState("tutti");
  // Modal "Invio per agente"
  const [agentiInRitardo, setAgentiInRitardo] = useState([]);
  const [showAgentiModal, setShowAgentiModal] = useState(false);
  const [expandedAg, setExpandedAg] = useState(null);
  const [selectedClienti, setSelectedClienti] = useState({});  // {agente_email: Set(cliente_id)}
  const [sendingMirato, setSendingMirato] = useState(false);

  const load = useCallback(async () => {
    const r = await auth("/crm/promemoria-config");
    if (r.ok) setCfg(await r.json());
    const r2 = await auth("/crm/hotel-spa-killswitch");
    if (r2.ok) setKillSwitch((await r2.json()).disabled);
  }, []);

  const loadWaPending = useCallback(async () => {
    setLoadingWa(true);
    const r = await auth("/crm/promemoria-visita/whatsapp-pending");
    setLoadingWa(false);
    if (r.ok) { const d = await r.json(); setWaPending(d.items || []); }
  }, []);

  const loadVisite = useCallback(async () => {
    const r = await auth("/crm/promemoria-visita/visite-programmate");
    if (r.ok) { const d = await r.json(); setVisite(d.items || []); }
  }, []);

  const loadStorico = useCallback(async () => {
    const params = new URLSearchParams({ limit: "100", tipo: storicoFilter });
    if (storicoFeedback && storicoFeedback !== "all") params.append("feedback_only", storicoFeedback);
    if (storicoSearch.trim()) params.append("destinatario", storicoSearch.trim());
    const r = await auth(`/crm/promemoria-visita/storico-invii?${params.toString()}`);
    if (r.ok) {
      const d = await r.json();
      setStorico(d.items || []);
      if (d.stats) setStoricoStats(d.stats);
    }
  }, [storicoFilter, storicoFeedback, storicoSearch]);

  useEffect(() => { loadStorico(); }, [loadStorico]);

  const loadDestinatari = useCallback(async () => {
    const r = await auth("/crm/promemoria-visita/destinatari");
    if (r.ok) { const d = await r.json(); setDestinatari(d.items || []); }
  }, []);

  const toggleDestinatario = async (email, currentValue) => {
    const r = await auth("/crm/promemoria-visita/destinatari/toggle", { method: "POST", body: JSON.stringify({ email, riceve_promemoria: !currentValue }) });
    if (r.ok) {
      setDestinatari((prev) => prev.map((d) => d.email === email ? { ...d, riceve_promemoria: !currentValue } : d));
      toast.success(!currentValue ? `✅ ${email} ora riceve i promemoria` : `🔕 ${email} ora è escluso dai promemoria`);
    }
  };

  const bulkToggleAll = async (target) => {
    const candidates = destinatari.filter((d) =>
      (filterTipo === "tutti" || d.tipo === filterTipo) &&
      d.riceve_promemoria !== target
    );
    if (!candidates.length) { toast.info("Nessun destinatario da aggiornare"); return; }
    if (!window.confirm(`${target ? "Attivare" : "Disattivare"} promemoria per ${candidates.length} destinatari?`)) return;
    for (const d of candidates) {
      // eslint-disable-next-line no-await-in-loop
      await auth("/crm/promemoria-visita/destinatari/toggle", { method: "POST", body: JSON.stringify({ email: d.email, riceve_promemoria: target }) });
    }
    toast.success(`✅ Aggiornati ${candidates.length} destinatari`);
    loadDestinatari();
  };

  const loadAgentiInRitardo = async () => {
    const r = await auth("/crm/promemoria-visita/candidates-by-agente?min_giorni=20");
    if (r.ok) {
      const d = await r.json();
      setAgentiInRitardo(d.items || []);
      setShowAgentiModal(true);
    } else {
      toast.error("Errore caricamento agenti");
    }
  };

  const toggleClienteSel = (agEmail, cid) => {
    setSelectedClienti((prev) => {
      const cur = new Set(prev[agEmail] || []);
      if (cur.has(cid)) cur.delete(cid);
      else cur.add(cid);
      return { ...prev, [agEmail]: cur };
    });
  };

  const selAllAgente = (ag) => {
    setSelectedClienti((prev) => ({ ...prev, [ag.agente_email]: new Set(ag.clienti.map((c) => c.cliente_id)) }));
  };

  const selNoneAgente = (ag) => {
    setSelectedClienti((prev) => ({ ...prev, [ag.agente_email]: new Set() }));
  };

  const inviaMirato = async (agEmail) => {
    const sel = selectedClienti[agEmail];
    if (!sel || !sel.size) { toast.error("Seleziona almeno un cliente"); return; }
    setSendingMirato(true);
    const r = await auth("/crm/promemoria-visita/send-mirato", {
      method: "POST", body: JSON.stringify({ cliente_ids: Array.from(sel) })
    });
    setSendingMirato(false);
    if (r.ok) {
      const d = await r.json();
      toast.success(`✅ ${d.sent}/${d.total} promemoria inviati a ${agEmail}`);
      setSelectedClienti((prev) => ({ ...prev, [agEmail]: new Set() }));
      loadAgentiInRitardo();
    } else {
      toast.error("Errore invio");
    }
  };

  const inviaTuttiAgente = async (ag) => {
    if (!window.confirm(`Inviare promemoria per TUTTI i ${ag.clienti.length} clienti in ritardo a ${ag.agente_email}?`)) return;
    setSendingMirato(true);
    const r = await auth("/crm/promemoria-visita/send-mirato", {
      method: "POST", body: JSON.stringify({ cliente_ids: ag.clienti.map((c) => c.cliente_id) })
    });
    setSendingMirato(false);
    if (r.ok) {
      const d = await r.json();
      toast.success(`✅ ${d.sent}/${d.total} promemoria inviati a ${ag.agente_email}`);
      loadAgentiInRitardo();
    }
  };

  useEffect(() => { load(); loadWaPending(); loadVisite(); loadDestinatari(); }, [load, loadWaPending, loadVisite, loadDestinatari]);

  const save = async () => {
    setSaving(true);
    const r = await auth("/crm/promemoria-config", { method: "POST", body: JSON.stringify(cfg) });
    setSaving(false);
    if (r.ok) { toast.success("✅ Configurazione salvata"); setCfg(await r.json()); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Errore"); }
  };

  const sendTest = async () => {
    const r = await auth("/crm/promemoria-visita/send-test", { method: "POST", body: JSON.stringify({
      destinatari: [testEmail],
      cliente_nome: "Centro Estetico Demo · Bellezza & Benessere",
      cliente_citta: "Bergamo",
      cliente_referente: "Maria Rossi",
      giorni_dall_ultima_visita: 30,
      agente_nome: "Test Agente",
      agente_phone: testPhone || "",
    })});
    if (r.ok) {
      const d = await r.json();
      const waUrl = (d.sent && d.sent[0] && d.sent[0].whatsapp_url) || "";
      toast.success(`📧 Email test inviata a ${testEmail}`);
      if (waUrl) {
        toast.success(`💬 Link WhatsApp generato — clicca per aprire`, {
          action: { label: "Apri WhatsApp", onClick: () => window.open(waUrl, "_blank") },
          duration: 10000,
        });
      }
      loadWaPending();
    }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Errore invio test"); }
  };

  const runNow = async () => {
    setRunning(true);
    const r = await auth("/crm/promemoria-visita/run-now", { method: "POST" });
    setRunning(false);
    if (r.ok) { const d = await r.json(); toast.success(`🚀 Scheduler eseguito: ${d.sent || 0} inviate, ${d.skipped || 0} saltate`); loadWaPending(); }
    else toast.error("Errore esecuzione");
  };

  const markAsSent = async (tokens) => {
    if (!tokens || !tokens.length) return;
    const r = await auth("/crm/promemoria-visita/whatsapp-mark-sent", { method: "POST", body: JSON.stringify({ tokens })});
    if (r.ok) {
      const d = await r.json();
      toast.success(`✅ ${d.marked} promemoria WhatsApp marcati come inviati`);
      setSelectedTokens([]);
      loadWaPending();
    }
  };

  const toggleSelect = (token) => {
    setSelectedTokens((prev) => prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]);
  };

  const openAndMark = (item) => {
    window.open(item.whatsapp_url, "_blank", "noopener,noreferrer");
    setTimeout(() => markAsSent([item.token]), 600);
  };

  const toggleKill = async () => {
    const newVal = !killSwitch;
    const r = await auth("/crm/hotel-spa-killswitch", { method: "POST", body: JSON.stringify({ enabled: newVal }) });
    if (r.ok) { setKillSwitch(newVal); toast.success(newVal ? "⛔ Invio Hotel/SPA BLOCCATO" : "✅ Invio Hotel/SPA RIATTIVATO"); }
  };

  if (!cfg) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-rose-400" /></div>;

  const Field = ({ label, value, onChange, type = "text", as = "input", help }) => (
    <div>
      <label className="block text-xs font-semibold text-[#7A4856] uppercase tracking-wider mb-1">{label}</label>
      {as === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows="3"
          className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm focus:outline-none focus:border-[#B87A8A]" />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(type === "number" ? parseInt(e.target.value) : e.target.value)}
          className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm focus:outline-none focus:border-[#B87A8A]" />
      )}
      {help && <p className="text-[11px] text-gray-500 mt-1">{help}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/crm" className="text-[#7A4856] hover:underline text-sm" data-testid="back-crm">
          <ArrowLeft className="w-4 h-4 inline" /> Dashboard CRM
        </Link>
        <h1 className="text-3xl font-bold text-[#5A3A3A] mt-3 mb-4">🔔 Configurazione Promemoria Visite</h1>

        {/* Kill-switch Hotel/SPA */}
        <div className={`rounded-2xl border-2 p-4 mb-6 ${killSwitch ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300"}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-sm">{killSwitch ? "⛔ Invio Email Hotel/SPA BLOCCATO" : "✅ Invio Email Hotel/SPA Attivo"}</h3>
              <p className="text-xs text-gray-600 mt-1">Disattiva l'invio di email dalla campagna Hotel & Luxury SPA quando la campagna è terminata.</p>
            </div>
            <button onClick={toggleKill} data-testid="btn-toggle-kill" className={`px-4 py-2 rounded-full text-sm font-semibold ${killSwitch ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
              <Power className="w-4 h-4 inline mr-1" /> {killSwitch ? "Riattiva invio" : "Blocca invio"}
            </button>
          </div>
        </div>

        {/* Promemoria config */}
        <div className="bg-white rounded-2xl border border-rose-100 p-6 space-y-4">
          {/* On/off */}
          <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-[#5A3A3A]">Promemoria automatici</h3>
              <p className="text-xs text-gray-600">Quando attivi, ogni mattina alle 09:00 il sistema invia email agli agenti per clienti inattivi.</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={cfg.enabled !== false} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="sr-only peer" data-testid="cfg-enabled" />
              <div className="relative w-14 h-7 bg-gray-300 peer-checked:bg-[#B87A8A] rounded-full transition">
                <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition ${cfg.enabled !== false ? "translate-x-7" : ""}`}></div>
              </div>
            </label>
          </div>

          {/* Soglie */}
          <Field label="Soglie giorni (separate da virgola)" value={(cfg.soglie_giorni || []).join(",")} onChange={(v) => setCfg({ ...cfg, soglie_giorni: v.split(",").map(x => parseInt(x.trim())).filter(x => !isNaN(x)) })} help="Es: 20,40,60,90 → invia mail+WhatsApp dopo 20, 40, 60, 90 giorni dall'ultimo ordine. Sono anche i chip mostrati all'agente quando sceglie 'tra quanti giorni vuoi il prossimo promemoria'." />

          {/* Target */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.send_to_agenti !== false} onChange={(e) => setCfg({ ...cfg, send_to_agenti: e.target.checked })} data-testid="cfg-agenti" className="w-4 h-4 accent-[#B87A8A]" />
              <span className="text-sm font-medium">👤 Invia agli Agenti</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.send_to_distributori !== false} onChange={(e) => setCfg({ ...cfg, send_to_distributori: e.target.checked })} data-testid="cfg-distributori" className="w-4 h-4 accent-[#B87A8A]" />
              <span className="text-sm font-medium">🏢 Invia ai Distributori</span>
            </label>
          </div>

          {/* BCC ADMIN */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="block text-sm font-semibold text-blue-900 mb-1">📩 Copia all'admin (BCC) di ogni email inviata</label>
            <input type="email" value={cfg.bcc_admin_email || ""} onChange={(e) => setCfg({ ...cfg, bcc_admin_email: e.target.value })}
              placeholder="es. admin@odracademy.it (vuoto = nessuna copia)" data-testid="cfg-bcc-admin"
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm" />
            <p className="text-[11px] text-blue-700 mt-1">L'admin riceverà una copia silenziosa di TUTTI gli invii (auto, mirati, test). Per disattivare lascia vuoto.</p>
          </div>

          {/* WhatsApp toggle */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <div>
                <span className="text-sm font-semibold text-green-900">💬 Invia anche su WhatsApp</span>
                <p className="text-xs text-green-700 mt-0.5">Genera un link <code>wa.me</code> pronto da aprire — usa il numero <b>phone</b> dell'agente (Profilo &gt; Telefono). Apparirà nella sezione "Coda WhatsApp" sotto.</p>
              </div>
              <input type="checkbox" checked={cfg.send_whatsapp !== false} onChange={(e) => setCfg({ ...cfg, send_whatsapp: e.target.checked })} data-testid="cfg-whatsapp" className="w-5 h-5 accent-green-600" />
            </label>
            {cfg.send_whatsapp !== false && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-green-900 uppercase tracking-wider mb-1">Testo messaggio WhatsApp</label>
                <textarea value={cfg.whatsapp_template || ""} onChange={(e) => setCfg({ ...cfg, whatsapp_template: e.target.value })} rows="6"
                  className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm font-mono focus:outline-none focus:border-green-500"
                  data-testid="cfg-whatsapp-template" />
                <p className="text-[11px] text-green-700 mt-1">Variabili: {"{agente_nome}"} {"{cliente_nome}"} {"{giorni}"} {"{citta_line}"} · Markdown WhatsApp supportato (*grassetto* _corsivo_)</p>
              </div>
            )}
          </div>

          <Field label="Oggetto email" value={cfg.subject_template} onChange={(v) => setCfg({ ...cfg, subject_template: v })} help="Variabili: {cliente_nome} {giorni}" />
          <Field label="Testo introduzione" value={cfg.body_intro} onChange={(v) => setCfg({ ...cfg, body_intro: v })} as="textarea" help="Variabili: {giorni} · HTML supportato" />
          <Field label="Messaggio sul feedback" value={cfg.body_feedback} onChange={(v) => setCfg({ ...cfg, body_feedback: v })} as="textarea" />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Testo bottone 'Già passato'" value={cfg.btn_visited_label} onChange={(v) => setCfg({ ...cfg, btn_visited_label: v })} />
            <Field label="Testo bottone 'Passerò prossimamente'" value={cfg.btn_will_visit_label} onChange={(v) => setCfg({ ...cfg, btn_will_visit_label: v })} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Colore primario" value={cfg.color_primary} onChange={(v) => setCfg({ ...cfg, color_primary: v })} help="Es: #B87A8A" />
            <Field label="Colore scuro" value={cfg.color_dark} onChange={(v) => setCfg({ ...cfg, color_dark: v })} />
            <Field label="Colore brand" value={cfg.color_brand} onChange={(v) => setCfg({ ...cfg, color_brand: v })} />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-rose-100">
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-[#B87A8A] text-white rounded-full font-medium hover:bg-[#a06978] disabled:opacity-50" data-testid="btn-save-cfg">
              {saving ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Save className="w-4 h-4 inline mr-1" />}
              Salva configurazione
            </button>
          </div>
        </div>

        {/* Test invio + run-now */}
        <div className="bg-white rounded-2xl border border-rose-100 p-6 mt-4">
          <h2 className="font-bold text-[#5A3A3A] mb-3">🧪 Test & Esecuzione</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Email destinatario test"
              className="px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="test-email" />
            <input type="tel" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Telefono per WhatsApp test (es. 3331234567)"
              className="px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="test-phone" />
          </div>
          <button onClick={sendTest} className="w-full px-4 py-2 bg-[#7A4856] text-white rounded-full text-sm font-medium hover:bg-[#5A3A3A] mb-3" data-testid="btn-send-test">
            <Send className="w-4 h-4 inline mr-1" /> Invia mail + genera link WhatsApp di test
          </button>
          <button onClick={runNow} disabled={running} className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-full font-medium hover:opacity-90 disabled:opacity-50" data-testid="btn-run-now">
            {running ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Bell className="w-4 h-4 inline mr-1" />}
            🚀 Esegui scheduler ORA (forza controllo clienti inattivi)
          </button>
          <button onClick={loadAgentiInRitardo} className="w-full mt-2 px-4 py-3 bg-gradient-to-r from-rose-500 to-rose-700 text-white rounded-full font-medium hover:opacity-90" data-testid="btn-invio-per-agente">
            📤 Invio mirato per agente (clienti in ritardo)
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">Lo scheduler gira automaticamente ogni giorno alle 09:00. Qui puoi forzarlo manualmente.</p>
        </div>

        {/* MODAL "INVIO PER AGENTE" */}
        {showAgentiModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAgentiModal(false)}>
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-rose-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#5A3A3A]">📤 Invio mirato per agente</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{agentiInRitardo.length} agenti con clienti in ritardo · clienti ordinati per ultima data ordine</p>
                </div>
                <button onClick={() => setShowAgentiModal(false)} className="text-2xl text-gray-400 hover:text-gray-700" data-testid="btn-close-modal">×</button>
              </div>
              <div className="overflow-y-auto p-4 space-y-2">
                {agentiInRitardo.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nessun agente con clienti in ritardo</p>
                ) : agentiInRitardo.map((ag) => {
                  const isExp = expandedAg === ag.agente_email;
                  const selSize = (selectedClienti[ag.agente_email] || new Set()).size;
                  return (
                    <div key={ag.agente_email || "_no_"} className="border border-rose-100 rounded-xl overflow-hidden" data-testid={`agente-card-${ag.agente_email}`}>
                      <div className="p-3 bg-rose-50 flex items-center justify-between gap-2 flex-wrap cursor-pointer hover:bg-rose-100" onClick={() => setExpandedAg(isExp ? null : ag.agente_email)}>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#5A3A3A]">👤 {ag.agente_name || ag.agente_email}</p>
                          <p className="text-xs text-gray-600">{ag.agente_email} {ag.agente_phone && `· 📱 ${ag.agente_phone}`}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-bold">{ag.count_clienti} clienti</span>
                          {ag.count_rossi > 0 && <span className="px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">🔴 {ag.count_rossi}</span>}
                          <span className="text-xs text-gray-500">max {ag.max_giorni}gg</span>
                          <span className="text-lg text-gray-400">{isExp ? "▾" : "▸"}</span>
                        </div>
                      </div>
                      {isExp && (
                        <div className="bg-white p-3 space-y-1.5 border-t border-rose-100">
                          <div className="flex gap-2 mb-2 flex-wrap items-center">
                            <button onClick={() => selAllAgente(ag)} className="px-2 py-1 text-xs bg-rose-100 text-rose-800 rounded-full">✓ Tutti</button>
                            <button onClick={() => selNoneAgente(ag)} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">✗ Nessuno</button>
                            <span className="text-xs text-gray-500 ml-auto">{selSize} selezionati</span>
                            <button onClick={() => inviaMirato(ag.agente_email)} disabled={!selSize || sendingMirato} className="px-3 py-1 text-xs bg-rose-600 text-white rounded-full font-medium hover:bg-rose-700 disabled:opacity-40" data-testid={`btn-send-sel-${ag.agente_email}`}>
                              {sendingMirato ? <Loader2 className="w-3 h-3 inline animate-spin" /> : "📨"} Invia selezionati ({selSize})
                            </button>
                            <button onClick={() => inviaTuttiAgente(ag)} disabled={sendingMirato} className="px-3 py-1 text-xs bg-amber-600 text-white rounded-full font-medium hover:bg-amber-700 disabled:opacity-40" data-testid={`btn-send-all-${ag.agente_email}`}>
                              ⚡ Invia tutti ({ag.count_clienti})
                            </button>
                          </div>
                          {ag.clienti.map((c) => {
                            const sel = (selectedClienti[ag.agente_email] || new Set()).has(c.cliente_id);
                            const colorClass = {red: "bg-red-100 text-red-800", orange: "bg-orange-100 text-orange-800", yellow: "bg-amber-100 text-amber-800", green: "bg-emerald-100 text-emerald-800"}[c.colore] || "bg-gray-100";
                            return (
                              <label key={c.cliente_id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-rose-25 ${sel ? "bg-rose-50" : ""}`}>
                                <input type="checkbox" checked={sel} onChange={() => toggleClienteSel(ag.agente_email, c.cliente_id)} className="w-4 h-4 accent-rose-600" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#5A3A3A] truncate">{c.ragione_sociale}</p>
                                  <p className="text-xs text-gray-500">{c.citta} · ultimo ordine: {c.ultimo_ordine_data} · € {(c.ultimo_ordine_importo || 0).toFixed(2)}</p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${colorClass}`}>{c.giorni_inattivo}gg</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* DESTINATARI OPT-IN / OPT-OUT */}
        <div className="bg-white rounded-2xl border border-blue-200 p-6 mt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-bold text-blue-900">📋 Destinatari promemoria ({destinatari.length})</h2>
            <div className="flex gap-2 flex-wrap">
              <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} className="px-2 py-1 text-xs border border-blue-300 rounded-full" data-testid="filter-tipo">
                <option value="tutti">Tutti</option>
                <option value="agente">Solo agenti</option>
                <option value="distributore">Solo distributori</option>
              </select>
              <select value={filterStato} onChange={(e) => setFilterStato(e.target.value)} className="px-2 py-1 text-xs border border-blue-300 rounded-full" data-testid="filter-stato">
                <option value="tutti">Tutti gli stati</option>
                <option value="on">✅ Riceve</option>
                <option value="off">🔕 Escluso</option>
              </select>
              <button onClick={() => bulkToggleAll(true)} className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full hover:bg-green-200">✅ Attiva tutti (filtro)</button>
              <button onClick={() => bulkToggleAll(false)} className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded-full hover:bg-red-200">🔕 Disattiva tutti (filtro)</button>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-3">Per ogni destinatario decidi se riceverà i promemoria automatici. Lo scheduler salta chi è "Escluso" (🔕).</p>
          {destinatari.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nessun agente/distributore in DB.</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="destinatari-list">
              {destinatari
                .filter((d) => filterTipo === "tutti" || d.tipo === filterTipo)
                .filter((d) => filterStato === "tutti" || (filterStato === "on" ? d.riceve_promemoria : !d.riceve_promemoria))
                .map((d) => (
                  <div key={d.email} className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border ${d.riceve_promemoria ? "bg-blue-50 border-blue-100" : "bg-gray-50 border-gray-200 opacity-70"}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#5A3A3A] truncate">
                        {d.tipo === "agente" ? "👤" : "🏢"} {d.name || d.email.split("@")[0]}
                        <span className="text-xs font-normal text-gray-500 ml-2">{d.email}</span>
                      </p>
                      {d.phone && <p className="text-[11px] text-gray-500">📱 {d.phone}</p>}
                    </div>
                    <button onClick={() => toggleDestinatario(d.email, d.riceve_promemoria)}
                      data-testid={`toggle-dest-${d.email}`}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${d.riceve_promemoria ? "bg-green-500" : "bg-gray-300"}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${d.riceve_promemoria ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* VISITE PROGRAMMATE DAGLI AGENTI */}
        <div className="bg-white rounded-2xl border border-purple-200 p-6 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-purple-900">📅 Visite programmate dagli agenti ({visite.length})</h2>
            <button onClick={loadVisite} className="px-3 py-1.5 text-xs bg-white border border-purple-300 text-purple-700 rounded-full hover:bg-purple-50" data-testid="btn-reload-visite">🔄 Ricarica</button>
          </div>
          {visite.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nessuna visita programmata. Appariranno qui quando un agente clicca "📅 Passerò prossimamente" nell'email e sceglie una data.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="visite-list">
              {visite.map((v) => {
                const dataLabel = v.data_visita_prevista ? v.data_visita_prevista.split("-").reverse().join("/") : "—";
                const isToday = v.data_visita_prevista === new Date().toISOString().slice(0, 10);
                return (
                  <div key={v.token} className={`p-3 rounded-lg border ${isToday ? "bg-amber-50 border-amber-300" : "bg-purple-50 border-purple-100"}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#5A3A3A]">📍 {v.cliente_nome} {v.cliente_citta && <span className="font-normal text-gray-500">· {v.cliente_citta}</span>}</p>
                        <p className="text-xs text-gray-600 mt-0.5">👤 <b>{v.agente_email}</b></p>
                        {v.nota_visita && <p className="text-xs text-purple-800 mt-1 italic">📝 {v.nota_visita}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${isToday ? "bg-amber-500 text-white" : "bg-purple-600 text-white"}`}>
                          {isToday ? "⏰ OGGI" : ""} {dataLabel}
                        </span>
                        {v.reminder_inviato && <span className="text-[10px] text-green-700">✅ Reminder inviato</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-gray-500 mt-3">💡 Le visite di OGGI ricevono un reminder automatico (email + WhatsApp) all'agente alle 08:00.</p>
        </div>

        {/* STORICO INVII */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-bold text-slate-800">📜 Storico invii ({storico.length})</h2>
            <div className="flex gap-2 flex-wrap items-center">
              <input
                value={storicoSearch}
                onChange={(e) => setStoricoSearch(e.target.value)}
                placeholder="🔍 Cerca agente (es. di.martino)"
                className="px-3 py-1 text-xs border border-slate-300 rounded-full w-56"
                data-testid="filter-storico-search"
              />
              <select value={storicoFeedback} onChange={(e) => setStoricoFeedback(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded-full" data-testid="filter-storico-feedback">
                <option value="all">Tutti i feedback</option>
                <option value="any">📬 Solo risposte ricevute</option>
                <option value="visited">✅ Solo Visitati</option>
                <option value="will_visit">📅 Solo Programmati</option>
                <option value="pending">⏳ Solo In attesa</option>
              </select>
              <select value={storicoFilter} onChange={(e) => setStoricoFilter(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded-full" data-testid="filter-storico-tipo">
                <option value="all">Tutti i tipi</option>
                <option value="auto">🤖 Solo automatici</option>
                <option value="manual">🖐️ Solo manuali</option>
                <option value="test">🧪 Solo test</option>
              </select>
              <button onClick={loadStorico} className="px-3 py-1 text-xs bg-slate-100 text-slate-800 rounded-full hover:bg-slate-200">🔄 Ricarica</button>
            </div>
          </div>

          {/* Statistiche risposte */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="storico-stats">
            <div className="bg-slate-50 rounded-lg p-2 text-center">
              <p className="text-[10px] uppercase text-slate-500">Totale Invii</p>
              <p className="text-xl font-bold text-slate-800" data-testid="stat-total">{storicoStats.total}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2 text-center border border-emerald-200">
              <p className="text-[10px] uppercase text-emerald-700">Risposte Ricevute</p>
              <p className="text-xl font-bold text-emerald-700" data-testid="stat-feedback">{storicoStats.feedback_received}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-2 text-center border border-green-200">
              <p className="text-[10px] uppercase text-green-700">✅ Visitati</p>
              <p className="text-xl font-bold text-green-700" data-testid="stat-visited">{storicoStats.visited}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 text-center border border-blue-200">
              <p className="text-[10px] uppercase text-blue-700">📅 Programmati</p>
              <p className="text-xl font-bold text-blue-700" data-testid="stat-will-visit">{storicoStats.will_visit}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-2 text-center border border-amber-200">
              <p className="text-[10px] uppercase text-amber-700">⏳ In attesa</p>
              <p className="text-xl font-bold text-amber-700" data-testid="stat-pending">{storicoStats.pending}</p>
            </div>
          </div>

          {storico.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nessun invio per i filtri selezionati.</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="storico-list">
              {storico.map((s) => {
                const tipoLabel = { auto: "🤖 AUTO", manual: "🖐️ MIRATO", test: "🧪 TEST" }[s.tipo_invio] || "📧";
                const tipoColor = { auto: "bg-blue-100 text-blue-800", manual: "bg-rose-100 text-rose-800", test: "bg-amber-100 text-amber-800" }[s.tipo_invio] || "bg-gray-100 text-gray-800";
                const dt = s.created_at ? new Date(s.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                const feedbackBadge = s.feedback === "visited" ? "✅ Visitato" : s.feedback === "will_visit" ? `📅 ${s.data_visita_prevista || "programmato"}` : "⏳ In attesa";
                return (
                  <div key={s.token} className="p-2.5 rounded-lg border border-slate-100 hover:bg-slate-25 flex items-start gap-2 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tipoColor} whitespace-nowrap`}>{tipoLabel}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#5A3A3A] truncate">{s.cliente_nome} {s.cliente_citta && <span className="text-xs text-gray-500">· {s.cliente_citta}</span>}</p>
                      <p className="text-xs text-gray-600">→ <b>{s.destinatario}</b> {s.agente_nome && <span className="text-gray-500">({s.agente_nome})</span>} · ⏱️ {s.giorni}gg</p>
                      <p className="text-[11px] text-gray-400">{dt} {s.bcc_admin_email && <span className="text-blue-600">· BCC: {s.bcc_admin_email}</span>}</p>
                    </div>
                    <span className="text-[11px] text-gray-700 whitespace-nowrap">{feedbackBadge}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-gray-500 mt-3">💡 Tutti gli invii email vengono <b>contrassegnati</b> con tipo: <b>AUTO</b> (scheduler giornaliero), <b>MIRATO</b> (invio admin manuale), <b>TEST</b>. L'admin riceve <b>BCC</b> di ogni email inviata.</p>
        </div>

        {/* CODA WHATSAPP */}
        <div className="bg-white rounded-2xl border border-green-200 p-6 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-green-900">💬 Coda WhatsApp ({waPending.length} pendenti)</h2>
            <div className="flex gap-2">
              <button onClick={loadWaPending} disabled={loadingWa} className="px-3 py-1.5 text-xs bg-white border border-green-300 text-green-700 rounded-full hover:bg-green-50" data-testid="btn-reload-wa">
                {loadingWa ? <Loader2 className="w-3 h-3 inline animate-spin" /> : "🔄"} Ricarica
              </button>
              {selectedTokens.length > 0 && (
                <button onClick={() => markAsSent(selectedTokens)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-full font-medium hover:bg-green-700" data-testid="btn-mark-selected">
                  <CheckCircle2 className="w-3 h-3 inline mr-1" /> Marca {selectedTokens.length} come inviati
                </button>
              )}
            </div>
          </div>
          {waPending.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">Nessun link WhatsApp pendente. Quando lo scheduler invia promemoria con WhatsApp attivo, i link appariranno qui.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="wa-list">
              {waPending.map((p) => (
                <div key={p.token} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                  <input type="checkbox" checked={selectedTokens.includes(p.token)} onChange={() => toggleSelect(p.token)} className="w-4 h-4 accent-green-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#5A3A3A] truncate">📍 {p.cliente_nome} {p.cliente_citta ? `· ${p.cliente_citta}` : ""}</p>
                    <p className="text-xs text-gray-600 truncate">→ <b>{p.agente_email}</b> · 📱 {p.agente_phone || "(no tel)"} · ⏱️ {p.giorni}gg</p>
                  </div>
                  <button onClick={() => openAndMark(p)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-full font-medium hover:bg-green-700 flex items-center gap-1 whitespace-nowrap" data-testid={`btn-open-wa-${p.token}`}>
                    <MessageCircle className="w-3 h-3" /> Apri WA <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-500 mt-3">💡 Cliccando "Apri WA" si apre WhatsApp Web/App col messaggio precompilato. Premi "Invia" su WhatsApp; il sistema lo marca automaticamente come inviato.</p>
        </div>
      </div>
    </div>
  );
}
